<template>
  <div class="qai-property pl-4">
    <!-- Object type: render children recursively inside an expansion panel -->
    <v-expansion-panels v-if="definition.Type === 'Object'" flat class="mb-2">
      <v-expansion-panel>
        <v-expansion-panel-header class="py-1">
          <div>
            <strong>
              <span v-if="definition.Description" class="grey--text text--darken-1">
                {{ definition.Description }}
              </span>
            </strong>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-if="definition.Children && definition.Children.length">
            <QAISettingsProperty v-for="child in definition.Children" :key="child.Name" :definition="child"
              :value="getChildValue(child.Name)" :siblingValues="value || {}"
              @input="setChildValue(child.Name, $event)" />
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Array type: render each item with children, allow add/remove -->
    <div v-else-if="definition.Type === 'Array'" class="mb-2">
      <div class="d-flex align-center mb-1">
        <strong>
          <span v-if="definition.Description" class="grey--text text--darken-1 ml-2">
            {{ definition.Description }}
          </span>
        </strong>
        <v-spacer />
        <v-btn small color="primary" @click="addArrayItem">
          <v-icon small left>mdi-plus</v-icon> Add
        </v-btn>
      </div>
      <v-expansion-panels flat>
        <v-expansion-panel v-for="(item, index) in arrayValue" :key="index">
          <v-expansion-panel-header class="py-1">
            <div class="d-flex align-center">
              <span>{{ itemLabel(item, index) }}</span>
              <v-spacer />
              <v-btn icon x-small color="error" class="mr-2" @click.stop="removeArrayItem(index)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <QAISettingsProperty v-for="child in definition.Children" :key="child.Name" :definition="child"
              :value="getArrayItemChildValue(index, child.Name)" :siblingValues="arrayValue[index] || {}"
              @input="setArrayItemChildValue(index, child.Name, $event)" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Leaf types: resolve to the correct field component -->
    <div v-else class="mb-1">
      <component :is="fieldComponent" :value="value" :definition="effectiveDefinition"
        @input="$emit('input', $event)" />
    </div>
  </div>
</template>

<script>
import QAIFieldString from "./QAIFieldString.vue";
import QAIFieldPassword from "./QAIFieldPassword.vue";
import QAIFieldInteger from "./QAIFieldInteger.vue";
import QAIFieldDouble from "./QAIFieldDouble.vue";
import QAIFieldBoolean from "./QAIFieldBoolean.vue";
import QAIFieldStringList from "./QAIFieldStringList.vue";
import QAIFieldStringListFixed from "./QAIFieldStringListFixed.vue";
import QAIFieldStringListMultiple from "./QAIFieldStringListMultiple.vue";
import QAIFieldNote from "./QAIFieldNote.vue";

const TYPE_COMPONENT_MAP = {
  String: "QAIFieldString",
  Password: "QAIFieldPassword",
  Integer: "QAIFieldInteger",
  Double: "QAIFieldDouble",
  Boolean: "QAIFieldBoolean",
  StringList: "QAIFieldStringList",
  StringListFixed: "QAIFieldStringListFixed",
  StringListMultiple: "QAIFieldStringListMultiple",
  Note: "QAIFieldNote",
};

export default {
  name: "QAISettingsProperty",
  components: {
    QAIFieldString,
    QAIFieldPassword,
    QAIFieldInteger,
    QAIFieldDouble,
    QAIFieldBoolean,
    QAIFieldStringList,
    QAIFieldStringListFixed,
    QAIFieldStringListMultiple,
    QAIFieldNote,
  },
  props: {
    definition: { type: Object, required: true },
    value: { default: null },
    /** Key-value map of sibling property values (passed by parent for DependsOn resolution) */
    siblingValues: { type: Object, default: () => ({}) },
  },

  computed: {
    fieldComponent() {
      return TYPE_COMPONENT_MAP[this.definition.Type] || "QAIFieldString";
    },

    /**
     * If the definition has DependsOn + SelectionGroups, resolve the sibling's
     * current value and swap in the matching selection group as Selection.
     */
    effectiveDefinition() {
      const def = this.definition;
      if (!def.DependsOn || !def.SelectionGroups) return def;

      const parentKey = this.resolveKey(this.siblingValues, def.DependsOn);
      const parentValue = parentKey !== undefined ? this.siblingValues[parentKey] : undefined;
      const resolvedSelection = (parentValue != null && def.SelectionGroups[parentValue]) || {};

      return { ...def, Selection: resolvedSelection };
    },

    arrayValue() {
      return Array.isArray(this.value) ? this.value : [];
    },
  },

  methods: {
    /** Object helpers: read/write a child key on the value object */
    getChildValue(name) {
      if (this.value && typeof this.value === "object" && !Array.isArray(this.value)) {
        // Case-insensitive property lookup
        const key = this.resolveKey(this.value, name);
        return key !== undefined ? this.value[key] : undefined;
      }
      return undefined;
    },

    setChildValue(name, val) {
      const current = (this.value && typeof this.value === "object") ? { ...this.value } : {};
      const key = this.resolveKey(current, name) || name;
      current[key] = val;
      this.$emit("input", current);
    },

    /** Array helpers */
    getArrayItemChildValue(index, name) {
      const item = this.arrayValue[index];
      if (item && typeof item === "object") {
        const key = this.resolveKey(item, name);
        return key !== undefined ? item[key] : undefined;
      }
      return undefined;
    },

    setArrayItemChildValue(index, name, val) {
      const arr = [...this.arrayValue];
      const item = arr[index] ? { ...arr[index] } : {};
      const key = this.resolveKey(item, name) || name;
      item[key] = val;
      arr[index] = item;
      this.$emit("input", arr);
    },

    addArrayItem() {
      const newItem = {};
      if (this.definition.Children) {
        this.definition.Children.forEach(child => {
          newItem[child.Name] = this.parseDefault(child);
        });
      }
      const arr = [...this.arrayValue, newItem];
      this.$emit("input", arr);
    },

    removeArrayItem(index) {
      const arr = [...this.arrayValue];
      arr.splice(index, 1);
      this.$emit("input", arr);
    },

    itemLabel(item, index) {
      // Try to find a "name" property to use as label
      if (item) {
        const nameKey = Object.keys(item).find(k => k.toLowerCase() === "name");
        if (nameKey && item[nameKey]) return item[nameKey];
      }
      return `Item ${index + 1}`;
    },

    parseDefault(def) {
      if (!def.DefaultValue && def.DefaultValue !== 0) return def.Type === "Boolean" ? false : "";
      switch (def.Type) {
        case "Boolean": return def.DefaultValue === "True" || def.DefaultValue === true;
        case "Integer": return parseInt(def.DefaultValue, 10) || 0;
        case "Double": return parseFloat(def.DefaultValue) || 0;
        default: return def.DefaultValue;
      }
    },

    /**
     * Case-insensitive key resolution.
     * The Configuration keys may differ in casing from Definition names.
     */
    resolveKey(obj, name) {
      if (!obj || typeof obj !== "object") return undefined;
      // Exact match first
      if (name in obj) return name;
      // Case-insensitive
      const lower = name.toLowerCase();
      return Object.keys(obj).find(k => k.toLowerCase() === lower);
    },
  },
};
</script>

<style scoped>
.qai-property {
  width: 100%;
}
</style>
